/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { alpha, createTheme, useTheme, ThemeOptions } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import React from 'react';

type PaletteMode = 'light' | 'dark';

// for green theme button
const greenBase = '#a6e0d5';
const greenMain = alpha(greenBase, 0.7);
const grayMain = '#e6edf5';

// Needed to make typescript happy with adding the new 'white' color
declare module '@mui/material/styles' {
  interface Palette {
    white: Palette['primary'];
    lightgray: Palette['primary'];
    darkgray: Palette['primary'];
    green: Palette['primary'];
  }
  interface PaletteOptions {
    white: PaletteOptions['primary'];
    lightgray: PaletteOptions['primary'];
    darkgray: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true;
    lightgray: true;
    darkgray: true;
    green: true;
  }
  interface ButtonPropsVariantOverrides {
    flat: true;
    link: true;
    contained: true;
    submit: true;
    return: true;
    styledToggle: true;
    custom: true;
    copy: true;
    compact: true;
  }
}
declare module '@mui/material/styles' {
  interface TypographyVariants {
    poster: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    poster?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    customisedStandard: true;
    customisedBase: true;
    mrnTypography: true;
  }
}

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    green: true;
  }
}

declare module '@mui/material/Tabs' {
  interface TabsPropsIndicatorColorOverrides {
    white: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    transparent: true;
  }
}
export const ColorModeContext = React.createContext({
  toggleColorMode: () => {
    // This is intentional
  },
});

/* How to theme.  See:
  https://mui.com/customization/palette/
*/
export const getTheme = (mode: PaletteMode, smallWidthDevice: boolean = false) =>
  ({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#2e96e5' : 'rgb(61, 58, 98)', //#3d3a62
        //Contrast text for dark blue is white
        contrastText: mode === 'dark' ? 'white !important' : 'white',
      },
      secondary: {
        main: mode === 'dark' ? '#e8eaec' : '#A6E0D5',
        contrastText: mode === 'dark' ? '#2A4Bc2' : 'rgba(0, 0, 0, 0.87)', // Same as $lightblue in _colors.scss
      },
      white: {
        main: mode === 'dark' ? '#444444' : '#ffffff',
        contrastText: mode === 'dark' ? 'white' : '#2A8BF2', // Same as $lightblue in _colors.scss
      },
      lightgray: {
        main: mode === 'dark' ? '#1a1a1b' : '#F8FAFC',
        contrastText: mode === 'dark' ? '#e8eaec' : '#707C97',
      },
      darkgray: {
        main: mode === 'dark' ? '#1a1a1b' : grayMain,
        light: alpha(mode === 'dark' ? '#1a1a1b' : grayMain, 0.5),
        dark: alpha(mode === 'dark' ? '#1a1a1b' : grayMain, 1),
        contrastText: mode === 'dark' ? '#e8eaec' : '#707C97',
      },
      green: {
        //For now same in light and dark
        main: greenMain,
        light: alpha(greenBase, 0.4),
        dark: alpha(greenBase, 0.9),
        contrastText: '#3D3A62',
      },
    },
    typography: {
      fontFamily: 'Helvetica Neue, Segoe UI, Roboto',
      fontSize: 14,
      fontWeight: '700',
      color: 'var(--indigonavyblue)',
      button: {
        textTransform: 'none',
      },
    },
    zIndex: {
      // Add to all the zindexes, to make them above pendo icons
      mobileStepper: 20000 + 1000,
      speedDial: 20000 + 1050,
      appBar: 20000 + 1100,
      drawer: 20000 + 1200,
      modal: 20000 + 1300,
      snackbar: 20000 + 1400,
      tooltip: 20000 + 1500,
      feedback: 20000 + 1000,
      commentForm: 20000 + 1000,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            lineHeight: 'normal',
          },
        },
        variants: [
          {
            props: { variant: 'flat' },
            style: (theme: any) => {
              const currentColor = theme.theme.palette[theme.ownerState.color];
              return {
                backgroundColor: currentColor.main,
                color: currentColor.contrastText,
                border: '1px solid #D4E3F2',
                padding: '5px 15px',
                fontWeight: 400,
                fontSize: 14,
                borderRadius: 6,
                '&:hover': {
                  backgroundColor: currentColor.dark,
                  color: currentColor.contrastText,
                },
              };
            },
          },
          {
            props: { variant: 'link' },
            style: (theme: any) => {
              const currentColor = theme.theme.palette[theme.ownerState.color];
              return {
                color: currentColor.contrastText,
                fontWeight: 400,
                fontSize: 14,
                borderRadius: 6,
                textDecorationLine: 'underline',
                '&:hover': {
                  backgroundColor: 'unset',
                },
              };
            },
          },
          {
            props: { variant: 'contained' },
            style: ({ theme, ownerState }: any) => {
              const currentColor = theme.palette[ownerState.color || 'primary'];
              const sizeStyles = {
                small: {
                  height: 36,
                  padding: '3px 10px',
                  fontSize: 12,
                },
                medium: {
                  height: 46,
                  padding: '5px 15px',
                  fontSize: 14,
                },
                large: {
                  height: 56,
                  padding: '8px 22px',
                  fontSize: 16,
                },
              } as const;

              const s = sizeStyles[(ownerState.size as keyof typeof sizeStyles) || 'medium'];
              return {
                backgroundColor: currentColor.main,
                color: currentColor.contrastText,
                fontWeight: 400,
                borderRadius: 6,
                ...s,
                '&:hover': {
                  backgroundColor: currentColor.dark,
                  color: currentColor.contrastText,
                },
                '&:disabled': {
                  backgroundColor: currentColor.light,
                  color: mode !== 'dark' ? '#C5C4CF' : '#707C97',
                },
              };
            },
          },
          {
            props: { variant: 'outlined' },
            style: () => {
              return {
                height: '46px',
                padding: '5px 15px',
                fontWeight: 400,
                fontSize: 14,
                borderRadius: 6,
              };
            },
          },
          {
            props: { variant: 'submit' },
            style: (theme: any) => {
              const currentColor = theme.theme.palette['green'];
              return {
                backgroundColor: currentColor.main,
                color: currentColor.contrastText,
                padding: '15px',
                width: 'max-content',
                fontWeight: 400,
                fontSize: 14,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: currentColor.dark,
                  color: currentColor.contrastText,
                },
                '&:disabled': {
                  backgroundColor: currentColor.light,
                },
              };
            },
          },
          {
            props: { variant: 'return' },
            style: (theme: any) => {
              const currentColor = theme.theme.palette['darkgray'];
              return {
                backgroundColor: currentColor.main,
                color: currentColor.contrastText,
                padding: '15px',
                width: 'max-content',
                fontWeight: 400,
                fontSize: 14,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: currentColor.dark,
                  color: currentColor.contrastText,
                },
                '&:disabled': {
                  backgroundColor: currentColor.light,
                },
              };
            },
          },
          {
            props: { variant: 'styledToggle' },
            style: (theme: any) => {
              return {
                fontSize: 12,
                fontWeight: 400,
                background: '#eeedff',
                color: '#363353',
                borderRadius: 0,
                padding: '2px 4px',
                float: 'right',
                margin: '11px 14px 8px auto',
                '&:hover': {
                  backgroundColor: '#dbdafb',
                },
                '&:active': {
                  background: '#c1bff0',
                },
              };
            },
          },
          {
            props: { variant: 'custom' },
            style: (theme: any) => {
              return {
                fontSize: 12,
                height: 34,
                fontWeight: 500,
                background: 'unset',
                borderRadius: 0,
                padding: '10px',
                paddingInline: '15px',
                boxShadow: 'none',
                border: '1px solid #cad7ea',
                color: '#3a5c84',

                '&:hover': {
                  color: '#254569',
                  border: '1px solid #89a2c7',
                },
                '&:focus': {
                  background: '#e2eaf6',
                  border: '1px solid 89a2c7',
                  color: '#254569',
                },
                '&.Mui-disabled': {
                  color: '#bbc0c6',
                  border: '#e2e8f1',
                },
                '@media (max-width: 950px)': {
                  maxWidth: '15ch',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  padding: '0px 10px',
                },
              };
            },
          },
          {
            props: { variant: 'copy' },
            style: (theme: any) => {
              return {
                fontSize: '10px',
                lineHeight: '20px',
                height: '24px',
                minWidth: '36px',
                padding: 0,
                fontWeight: 400,
                background: '#eff3f8',
                borderRadius: 0,
                borderTopLeftRadius: '2px',
                borderBottomLeftRadius: '2px',
                border: 'none',
                color: '#3a5c84',
                cursor: 'pointer',
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                display: 'flex',
                alignItems: 'center',
              };
            },
          },
          {
            props: { variant: 'compact' },
            style: ({ theme, ownerState }: any) => {
              const color = theme.palette[ownerState.color] || theme.palette.primary;
              return {
                fontSize: '13px',
                lineHeight: '15px',
                padding: '6px 8px',
                fontWeight: 500,
                background: color.main,
                borderRadius: 2,
                border: 'none',
                color: color.contrastText,
                cursor: 'pointer',
                fontFamily: 'Helvetica Neue, Arial, sans-serif',

                '&:hover': {
                  background: color.main,
                  color: color.contrastText,
                },
                '&.Mui-disabled': {
                  color: 'var(--softblue)',
                  background: 'var(--lightgray)',
                  cursor: 'not-allowed',
                },
              };
            },
          },
        ],
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 14,
            lineHeight: 1.3,
          },
        },
      },

      MuiTab: {
        variants: [
          {
            props: { className: 'cme' },
            style: (theme: any) => ({
              fontSize: '14px',
              backgroundColor: theme.ownerState.color === 'error' ? 'var(--editpublishedwarning)' : 'var(--brightgray)',
              border: theme.ownerState.color === 'error' ? '1px solid red' : '1px solid var(--white)',
              fontWeight: 400,
              color: theme.ownerState.color === 'error' ? 'red' : 'var(--black)',
              '&.Mui-selected': {
                backgroundColor: 'var(--white)',
                color: 'var(--black) !important',
                borderBottom: 'none',
                fontWeight: 600,
              },
              '&.MuiButtonBase-root': {
                padding: smallWidthDevice ? '6px 8px' : '12px 16px',
              },
            }),
          },
          {
            props: { className: 'welcomepagetab' },
            style: () => ({
              padding: '12px 36px',
              '&.Mui-selected': {
                color: 'var(--blue)',
              },
            }),
          },
        ],
      },

      MuiTabPanel: {
        variants: [
          {
            props: { className: 'cme' },
            style: () => ({
              padding: '0px',
              background: 'var(--white)',
            }),
          },
        ],
      },

      MuiAvatar: {
        variants: [
          {
            props: { className: 'cme' },
            style: () => ({
              height: '22px',
              borderRadius: '23px',
              flexShrink: '0',
              backgroundColor: 'var(--gray93)',
              display: 'inline-flex',
              marginLeft: '4px',
              color: 'var(--black)',
              fontSize: smallWidthDevice ? '8px' : '10px',
              fontWeight: '700',
              lineHeight: '18px',
            }),
          },
        ],
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            fontSize: 14,
            color: 'var(--gray37)',
          },
        },
        variants: [
          {
            props: { variant: 'customisedStandard' },
            style: () => {
              return {
                fontSize: '8px',
                color: 'var(--mrntypography)',
                paddingBottom: '5px',
                paddingTop: '2px',
                fontWeight: 400,
              };
            },
          },
          {
            props: {
              variant: 'customisedBase',
              style: () => {
                return {
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--indigonavyblue)',
                };
              },
            },
          },
          {
            props: {
              variant: 'mrnTypography',
              style: () => {
                return {
                  fontWeight: 500,
                  padding: 0,
                  fontSize: '8px',
                  color: 'var(--mrntypography)',
                  paddingBottom: '5px',
                  paddingTop: '2px',
                };
              },
            },
          },
        ],
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 48,
            height: 24,
            padding: 0,
            marginLeft: 10,
            marginTop: 2,
            borderRadius: 26 / 2,
          },
          switchBase: {
            padding: 0,
            marginTop: 2,
            transitionDuration: '300ms',
            color: 'white',
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: '#fff',
              marginLeft: 7,
            },
          },
          track: {
            backgroundColor: 'var(--audienceselectorbackground)',
            opacity: 1,
            '.Mui-checked.Mui-checked + &': {
              backgroundColor: '#65D4BF',
              opacity: 1,
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiFormControlLabel: {
        variants: [
          {
            props: { className: 'feedback' },
            style: () => ({
              // fill the active radio circle
              '& .MuiRadio-root  svg:nth-child(2)': {
                color: 'var(--feedback-radio)',
              },
              '&.MuiFormControlLabel-root': {
                justifyContent: 'space-between',
                color: 'var(--eastbay)',
                marginLeft: '12px',
              },
              '&.Mui-disabled': {
                color: 'var(--softblue)',
              },
            }),
          },
        ],
      },
      MuiChip: {
        variants: [
          {
            props: { variant: 'transparent' },
            style: () => ({
              display: 'flex',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              backgroundColor: 'transparent',
              paddingTop: '20px',
              paddingBottom: '20px',
              alignItems: 'center',

              '& .MuiChip-label': {
                width: '100%',
                padding: 'unset',
              },
            }),
          },
        ],
      },
    },
  } as ThemeOptions);

/**
 * This is a hook that will return the current theme and a callback to toggle the color mode from light mode to dark mode.
 *
 * Use like:
 *
 * ```ts
 *   const [mode, theme, toggleColorModeCallback] = usePaletteMode();
 *
 *   useEffect(() => {
 *       document.body.setAttribute('data-mode', mode);
 *    }, [mode]);
 *
 *    <div className="App">
 *      <ColorModeContext.Provider value={toggleColorModeCallback}>
 *        <ThemeProvider theme={theme}>
 *           (your app)
 *        </ThemeProvider>
 *      </ColorModeContext.Provider>
 *    </div>
 * ```
 *
 * DO NOT USE OUTSIDE OF THE APP COMPONENT.  If you want to know if we are in dark mode, ask via mui theme:
 * ```ts
 *  const darkMode = useIsDarkMode();
 * ```
 */
export function usePaletteMode(checkMediaQueryPrefersDark = false) {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const smallWidthDevice = useMediaQuery('(max-width: 600px)');

  const [mode, setMode] = React.useState<PaletteMode>(
    localStorage.getItem('darkMode') === 'dark'
      ? 'dark'
      : checkMediaQueryPrefersDark && prefersDarkMode
      ? 'dark'
      : 'light'
  );

  const toggleColorModeCallback = React.useMemo(
    () => ({
      // The dark mode switch would invoke this method
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          console.log('set mode to ', newMode);
          localStorage.setItem('darkMode', newMode);
          return newMode;
        });
      },
    }),
    []
  );
  const theme = React.useMemo(() => createTheme(getTheme(mode, smallWidthDevice)), [mode, smallWidthDevice]);
  return [mode, theme, toggleColorModeCallback] as const;
}

export function useIsDarkMode() {
  const theme = useTheme();
  return theme.palette.mode === 'dark';
}
